// @flow

import * as React from "react";
import { ReactFormField } from "@adrenalin/react-form";

import splitToNChunks from "../../modules/util/splitToNChunks";
import DatePickerField from "../DatePickerField";
import { drupalDate } from "../../constants/Validators";
import CheckboxGroup from "../CheckboxGroup";
import RadioGroup from "../RadioGroup";
import Accordion from "../Accordion";
import CustomField from "../CustomField";

import * as GTM from "../../modules/tag-manager";

import {
    type MergableStateSetterType,
    type MergableStateStateType,
} from "../../hooks/useMergableState";

const { useState, useEffect } = React;

/**
 * A helper class for dynamically creating react component for use inside a ReactForm wrapper.
 * We are using a helper class rather than standard components as ReactForm is unable
 * to dive through multiple layers of nested components. If we simply use standard functions
 * that return JSX, we can get it all to work together.
 *
 * Most of the power of this class comes from the shareable nature of hooks and specifically
 * the useMergableState hook that works closer to a tradition this.setState({}) function call.
 */
class SharedFilterFunctions {
    inputState: MergableStateStateType;
    setInputState: MergableStateSetterType;
    filterPageName: string;

    constructor(
        inputState: MergableStateStateType,
        setInputState: MergableStateSetterType,
        filterPageName: ?string
    ) {
        this.inputState = inputState;
        this.setInputState = setInputState;
        this.filterPageName = filterPageName || "Unkown Filter Page";

        GTM.setDatalayerVar("filter_page_name", this.filterPageName);
    }

    /** Hook for automatically keeping track of the number of checked inputs */
    useFilterCounter = (data: Array<any>): number => {
        const [count, setCount] = useState(0);

        // Setup the initial state
        useEffect(() => {
            let initialInputState = {};
            data.forEach((item: any) => {
                initialInputState[item.name] = item.active;
            });
            this.setInputState(initialInputState);
        }, [data]);

        // Runs whenever our input state changes
        // Calculates the totals for each of the main sections
        useEffect(() => {
            setCount(
                data.reduce((total: number, item: any): any => {
                    if (this.inputState[item.name]) {
                        return total + 1;
                    }
                    return total;
                }, 0)
            );
        }, [this.inputState]);

        return count;
    };

    /** Renders a group of checkboxes */
    renderCheckboxGroup = (
        options: Array<any>,
        columns: ?number = 1,
        title: string
    ): React.Node => {
        const chunks = splitToNChunks(options, columns);
        return (
            <CheckboxGroup>
                {chunks.map((options: any, chunkIndex: number): React.Node => {
                    return (
                        <div className="radio-group-col" key={chunkIndex}>
                            {options.map(
                                (option: any, index: number): React.Node => {
                                    return (
                                        <ReactFormField
                                            key={index}
                                            type="checkbox"
                                            label={option.label}
                                            name={option.name}
                                            value={option.value}
                                            id={option.name}
                                            defaultChecked={option.active}
                                            checked={
                                                this.inputState[option.name]
                                            }
                                            onChange={(event: any) => {
                                                this.setInputState({
                                                    [option.name]:
                                                        event.target.checked,
                                                });
                                                GTM.customEvent({
                                                    event:
                                                        "filter-checkbox-changed",
                                                    filter_checkbox_group: title,
                                                    filter_checkbox_label:
                                                        option.label,
                                                    filter_page_name: this
                                                        .filterPageName,
                                                });
                                            }}
                                            fieldComponent={CustomField}
                                            hideOptional
                                        ></ReactFormField>
                                    );
                                }
                            )}
                        </div>
                    );
                })}
            </CheckboxGroup>
        );
    };

    /** Renders a group of radio buttons */
    renderRadioGroup = (
        options: Array<any>,
        columns: number,
        title: string
    ): React.Node => {
        const chunks = splitToNChunks(options, columns);
        const [isDefault, setIsDefault] = useState(true);
        return (
            <RadioGroup>
                {chunks.map((options: Array<any>, colIndex: number): any => {
                    return (
                        <div className="radio-group-col" key={colIndex}>
                            {options.map(
                                (option: any, index: number): React.Node => {
                                    return (
                                        <ReactFormField
                                            key={index}
                                            type="radio"
                                            label={option.label}
                                            name={option.name}
                                            value={option.value}
                                            id={`${option.name}[${option.value}]`}
                                            defaultChecked={option.active}
                                            checked={
                                                isDefault
                                                    ? option.active
                                                    : this.inputState[
                                                          option.name
                                                      ] === option.value
                                            }
                                            onChange={(event: any) => {
                                                setIsDefault(false);
                                                this.setInputState({
                                                    [option.name]: option.value,
                                                });
                                                if (event.target.checked) {
                                                    GTM.customEvent({
                                                        event:
                                                            "filter-radio-changed",
                                                        filter_radio_group: title,
                                                        filter_radio_label:
                                                            option.label,
                                                        filter_page_name: this
                                                            .filterPageName,
                                                    });
                                                }
                                            }}
                                            fieldComponent={CustomField}
                                            hideOptional
                                        ></ReactFormField>
                                    );
                                }
                            )}
                        </div>
                    );
                })}
            </RadioGroup>
        );
    };

    /** Renders the title for use in accordions */
    renderAccordionTitle = (title: string, count: number): React.Node => {
        if (count === 0) {
            return title;
        } else {
            return (
                <span>
                    {title} <span style={{ opacity: 0.5 }}>({count})</span>
                </span>
            );
        }
    };

    /** Creates the data structure needed to pass to either renderAccordionRadioGroup or renderAccordionCheckboxGroup */
    createFilterGroup = (
        type: string = "",
        title: string = "",
        data: any[] = [],
        extra: {} = {}
    ): any => {
        return {
            type,
            title,
            data,
            ...extra,
        };
    };

    /** Renders a group of radios inside an accordion */
    renderAccordionRadioGroup = ({
        title,
        data,
        columns = 1,
    }: {
        title: string,
        data: any[],
        columns?: number,
    }): React.Node => {
        return (
            <Accordion title={this.renderAccordionTitle(title, 0)}>
                <div className="accordion-padded-content">
                    <div className="accordion-checkbox-group">
                        {this.renderRadioGroup(data, columns, title)}
                    </div>
                </div>
            </Accordion>
        );
    };

    /** Renders a group of checkboxes inside an accordion */
    renderAccordionCheckboxGroup = ({
        title,
        data,
        columns = 1,
    }: {
        title: string,
        data: any[],
        columns?: number,
    }): React.Node => {
        const counter = this.useFilterCounter(data);
        return (
            <Accordion title={this.renderAccordionTitle(title, counter)}>
                <div className="accordion-padded-content">
                    {this.renderCheckboxGroup(data, columns, title)}
                </div>
            </Accordion>
        );
    };

    /** Renders the action buttons (Submit/Reset) for display at the bottom of filter forms */
    renderFilterActions = (resetUrl: string): React.Node => {
        return (
            <div className="filters__actions">
                <button className="button button--blue" type="submit">
                    Apply Filters
                </button>
                <a href={resetUrl} className="button button--clear">
                    Reset Filters
                </a>
            </div>
        );
    };

    /** Renders a date field */
    renderDateField = (
        data: {
            name: string,
            label: string,
            required: boolean,
        },
        datePickersState: any,
        setDateFieldValue: (string, any) => void
    ): React.Node => {
        return (
            <ReactFormField
                {...data}
                type="text"
                className={"date-picker-input"}
                fieldComponent={DatePickerField}
                value={datePickersState[`datepicker-${data.name}`]}
                onChange={(event: any) => {
                    setDateFieldValue(
                        `datepicker-${data.name}`,
                        event.target.value
                    );
                }}
                validators={[drupalDate.ID]}
            ></ReactFormField>
        );
    };

    /** Renders a search field */
    renderSearchField = (name: string): React.Node => {
        return (
            <ReactFormField
                type="text"
                placeholder="Search for keywords"
                name={name}
                fieldComponent={CustomField}
                icon={"search"}
                value={this.inputState[name]}
                onChange={(event: any) => {
                    this.setInputState({
                        [name]: event.target.value,
                    });
                    GTM.setDatalayerVar(
                        "filter_search_term",
                        event.target.value
                    );
                }}
            ></ReactFormField>
        );
    };

    /**
     * Pass an array of groups created from the createFilterGroup() function to this to
     * automatically create a component looper of filter components nested in accordions
     */
    filterGroupLooper = (filterGroups: any[]): React.Node => {
        return filterGroups.map((group: any, loopIndex: number): any => {
            let el: React.Node;
            switch (group.type) {
                case "checkbox":
                    el = this.renderAccordionCheckboxGroup(group);
                    break;
                case "radio":
                    el = this.renderAccordionRadioGroup(group);
                    break;
                default:
                    el = <div></div>;
                    break;
            }
            return <React.Fragment key={loopIndex}>{el}</React.Fragment>;
        });
    };
}

export default SharedFilterFunctions;
